// ─── Public Status Page — Cloudflare Turnstile ───────────────────────
//
// THE RULE THIS FILE EXISTS TO ENFORCE: the Cloudflare script is injected ONLY
// when this component MOUNTS — that is, only when a visitor has actually opened
// the subscribe sheet. It must NEVER be referenced from status.html, from the
// page shell, or from any module the shell imports.
//
// Why that matters more here than anywhere else in PCP: this is the page people
// load WHEN THINGS ARE BROKEN. A third-party script in the shell would mean the
// status page cannot render until challenges.cloudflare.com resolves, connects
// and responds — from a corporate network that blocks it, from a country that
// blocks it, or during a Cloudflare incident of the exact kind this page is
// supposed to report. The status page must depend on nothing but itself.
//
// And when the widget cannot be used at all — no site key configured, the script
// blocked, a 6 s timeout, a render failure, a challenge error — we say so
// quietly and STILL ALLOW THE SUBMIT. A captcha is the weakest of the four
// defences on this form (the others being the server-side per-IP rate limit, the
// honeypot, the dwell floor and mandatory double opt-in, none of which a visitor
// can see or break), and none of them is worth making the page un-subscribable
// while a provider is down.

import { useEffect, useRef, useState } from 'react';
import { da } from '../i18n/da';

/** The slice of the Turnstile global we use. */
interface TurnstileApi {
  render: (element: HTMLElement, options: Record<string, unknown>) => string | undefined;
  remove: (widgetId: string) => void;
  reset: (widgetId?: string) => void;
}

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
/** Beyond this the visitor is just staring at a form that will not submit. */
const LOAD_TIMEOUT_MS = 6000;

/** One in-flight load for the whole document, cleared on failure so re-opening
 *  the sheet is a real retry rather than a replay of the same rejection. */
let loader: Promise<TurnstileApi> | null = null;

function turnstileGlobal(): TurnstileApi | null {
  return (window as unknown as { turnstile?: TurnstileApi }).turnstile ?? null;
}

function loadTurnstile(): Promise<TurnstileApi> {
  const existing = turnstileGlobal();
  if (existing) return Promise.resolve(existing);
  if (loader) return loader;

  const pending = new Promise<TurnstileApi>((resolve, reject) => {
    let script = document.querySelector<HTMLScriptElement>('script[data-sp-turnstile="1"]');
    if (!script) {
      script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.dataset.spTurnstile = '1';
      // A blocked or failed load must never take the page with it.
      script.addEventListener('error', () => reject(new Error('turnstile_script_failed')));
      script.addEventListener('load', () => {
        const api = turnstileGlobal();
        if (api) resolve(api);
        else reject(new Error('turnstile_missing'));
      });
      document.head.appendChild(script);
      return;
    }
    // A previous mount already appended it — attach to that same element.
    script.addEventListener('error', () => reject(new Error('turnstile_script_failed')));
    script.addEventListener('load', () => {
      const api = turnstileGlobal();
      if (api) resolve(api);
      else reject(new Error('turnstile_missing'));
    });
  });

  loader = pending.catch((error: unknown) => {
    loader = null;
    throw error;
  });
  return loader;
}

/** Reject rather than hang: a CDN that accepts the TCP connection and then goes
 *  quiet is the most common way a script "loads forever". */
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('turnstile_timeout')), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error instanceof Error ? error : new Error('turnstile_failed'));
      },
    );
  });
}

export interface TurnstileFieldProps {
  /** From the subscribe session. Empty ⇒ Turnstile is not configured at all. */
  siteKey: string;
  /** The solved token, or null when it expires / errors and must not be sent. */
  onToken: (token: string | null) => void;
  /** Fired once when the widget cannot be used — the form MUST stay submittable. */
  onUnavailable: () => void;
}

export function TurnstileField({ siteKey, onToken, onUnavailable }: TurnstileFieldProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  // Callbacks go through refs so an inline arrow in the parent cannot re-run the
  // effect — re-running it would tear down a solved widget and ask the visitor
  // to prove they are human a second time.
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;
  const onUnavailableRef = useRef(onUnavailable);
  onUnavailableRef.current = onUnavailable;

  useEffect(() => {
    let cancelled = false;
    let api: TurnstileApi | null = null;
    let widgetId: string | undefined;

    const giveUp = () => {
      if (cancelled) return;
      setUnavailable(true);
      onTokenRef.current(null);
      onUnavailableRef.current();
    };

    if (!siteKey) {
      giveUp();
      return () => {
        cancelled = true;
      };
    }

    void withTimeout(loadTurnstile(), LOAD_TIMEOUT_MS)
      .then((loaded) => {
        if (cancelled) return;
        const host = hostRef.current;
        if (!host) {
          giveUp();
          return;
        }
        api = loaded;
        widgetId = loaded.render(host, {
          sitekey: siteKey,
          theme: 'auto',
          // The widget's own UI language. The page's COPY is Danish (dates and
          // numbers still follow the reader's locale — see lib/format).
          language: 'da',
          callback: (token: string) => onTokenRef.current(token),
          // A challenge that errors out is treated exactly like a missing
          // provider: tell the visitor, and let them subscribe anyway.
          'error-callback': giveUp,
          'timeout-callback': giveUp,
          'expired-callback': () => {
            onTokenRef.current(null);
            if (widgetId && api) api.reset(widgetId);
          },
        });
        if (!widgetId) giveUp();
      })
      .catch(giveUp);

    return () => {
      cancelled = true;
      if (api && widgetId) {
        try {
          api.remove(widgetId);
        } catch {
          /* the widget's host node is already gone — nothing to clean up */
        }
      }
    };
  }, [siteKey]);

  return (
    <div className="sp-field">
      {/* Kept mounted even when hidden: the widget lives inside this node, and
          unmounting it out from under Cloudflare's own cleanup throws. */}
      <div ref={hostRef} className="sp-turnstile" hidden={unavailable} />
      {unavailable && <p className="sp-help">{da.captchaUnavailable}</p>}
    </div>
  );
}
